"use client";

import PlusSvgIcon from "@/shared/icons/PlusSvgIcon";
import {
  Box,
  createListCollection,
  Field,
  HStack,
  Icon,
  Input,
  Portal,
  Select,
  Text,
  Textarea,
  FileUpload,
  VStack,
  Button,
} from "@chakra-ui/react";
import { useLocale, useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { z } from "zod";
import { withMask } from "use-mask-input";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { sendHRMessage } from "@/services/submission";
import CheckSvgIcon from "@/shared/icons/CheckSvgIcon";
import { BaseDialog } from "../ui/dialog/BaseDialog";
import Image from "next/image";

interface Props {
  vacancies: { title: string }[];
}

const CareerForm: React.FC<Props> = ({ vacancies }) => {
  const t = useTranslations();
  const locale = (useLocale() as "ru" | "kz" | "en") ?? "ru";
  const [isSended, setIsSended] = useState(false);
  const [modalText, setModalText] = useState<string | null>(null);

  const vacanciesCollection = useMemo(() => {
    return createListCollection({
      items: (vacancies ?? []).map((v) => ({ value: v.title, label: v.title })),
    });
  }, [vacancies, locale]);
  
  const formSchema = z.object({
    full_name: z.string().min(1, t("checkout_first_name_required")),
    email: z.string().email(t("invalid_email")),
    phone: z.string().min(11, t("invalid_phone")),
    vacancy: z.string().min(1, t("select_vacancy")),
    resume: z.any().refine((file) => file instanceof File, {
        message: t("career_resume_required"),
      }),
    comment: z.string().optional(),
  });
  type FormValues = z.infer<typeof formSchema>;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      full_name: "",
      email: "",
      phone: "",
      vacancy: "",
      comment: "",
    },
  });

  const onSubmit = (data: FormValues) => {
    sendMutation.mutate(data);
  };

  const sendMutation = useMutation({
    mutationFn: (data: FormValues) => sendHRMessage(data),
    onSuccess: () => {
      reset();
      setIsSended(true);
      setModalText(t("career_sended"));
    },
    onError: () => {
      reset();
      setIsSended(true);
      setModalText(t("career_sended_error"));
    },
  });
  
  return (
    <VStack alignItems={"start"} gap={"40px"} position={"relative"}>
      <HStack
        align={"start"}
        flexDirection={{ base: "column-reverse", md: "row" }}
        w={"100%"}
        gap={0}
        mt={{ base: "0px", md: "40px" }}
        mb={{ base: "0", md: "40px" }}
      >
        <VStack
          gap={0}
          w={"full"}
          align={"start"}
          maxW={{ base: "100%", md: "50%" }}
          paddingBlock={{ base: "40px", md: "0px" }}
          pr={{ base: "16px", md: "40px" }}
          pl={{ base: "16px", md: "0px"}}
        >
          <VStack
            p={"16px"}
            gap={"16px"}
            align={"start"}
            w={"full"}
            border={"2px solid #EEEEEE"}
          >
            <Text textStyle="bold24" dangerouslySetInnerHTML={{ __html: t('career_form_title') }}>
            </Text>

            <VStack align={"start"} gap={"16px"} w={"full"}>
              {/* FULL NAME */}
              <Field.Root invalid={!!errors.full_name}>
                <Input
                  {...register("full_name")}
                  _placeholder={{
                    color: '#9E9F9D',
                    textTransform: 'uppercase',
                  }}
                  variant="flushed"
                  placeholder={t("full_name")}
                />
                <Field.ErrorText>{errors.full_name?.message}</Field.ErrorText>
              </Field.Root>

              {/* EMAIL */}
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <Field.Root invalid={!!errors.email}>
                    <Input
                      {...field}
                      _placeholder={{
                        color: '#9E9F9D',
                        textTransform: 'uppercase',
                      }}
                      variant="flushed"
                      placeholder={t("checkout_enter_email")}
                    />
                    <Field.ErrorText>{errors.email?.message}</Field.ErrorText>
                  </Field.Root>
                )}
              />

              {/* PHONE */}
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <Field.Root invalid={!!errors.phone}>
                    <Input
                      {...field}
                      variant="flushed"
                      _placeholder={{
                        color: '#9E9F9D',
                      }}
                      placeholder="+7 ___ ___ __ __"
                      ref={withMask("+7 999 999 99 99")}
                    />
                    <Field.ErrorText>{errors.phone?.message}</Field.ErrorText>
                  </Field.Root>
                )}
              />

              {/* TYPE SELECT */}
              <Controller
                name="vacancy"
                control={control}
                render={({ field }) => (
                  <Field.Root invalid={!!errors.vacancy}>
                    <Select.Root
                      color={!!field.value ? 'inherit' : '#9e9f9d'}
                      value={[field.value]}
                      onValueChange={({ value }) => {
                        field.onChange(
                          Array.isArray(value) ? value[0] : value,
                        );
                      }}
                      multiple={false}
                      collection={vacanciesCollection}
                      variant={"qrep_flushed"}
                      _placeholder={{
                        color: '#9e9f9d',
                        textTransform: 'uppercase',
                      }}
                    >
                      <Select.HiddenSelect />
                      <Select.Control>
                        <Select.Trigger>
                          <Select.ValueText textTransform={'uppercase'} placeholder={t("career_vacancy")} />
                        </Select.Trigger>
                        <Select.IndicatorGroup padding={0}>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>
                      <Portal>
                        <Select.Positioner>
                          <Select.Content>
                            {vacanciesCollection.items.map((vacancy) => (
                              <Select.Item item={vacancy} key={vacancy.value}>
                                {vacancy.label}
                                <Select.ItemIndicator>
                                  <Icon color="brand">
                                    <CheckSvgIcon />
                                  </Icon>
                                </Select.ItemIndicator>
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Portal>
                    </Select.Root>
                    <Field.ErrorText>{errors.vacancy?.message}</Field.ErrorText>
                  </Field.Root>
                )}
              />

              <Controller
                name="resume"
                control={control}
                render={({ field }) => (
                  <Field.Root
                    invalid={!!errors.resume}
                    color={!!field.value ? 'inherit' : 'darkgray'}
                    _placeholder={{
                      color: '#9e9f9d',
                      textTransform: 'uppercase',
                    }}
                  >
                    <FileUpload.Root
                      key={field.value ? field.value.name : 'empty'} // важный момент для reset()
                      position={'relative'}
                      maxFiles={1}
                      accept={[".pdf", ".doc", ".docx"]}
                      onFileChange={(details) => {
                        const file = details.acceptedFiles[0] ?? null;
                        field.onChange(file);
                      }}
                    >
                      <FileUpload.HiddenInput />
                      <Input variant="flushed" asChild>
                        <FileUpload.Trigger textTransform={'uppercase'}>
                          {field.value ? field.value.name : t('career_resume')}
                        </FileUpload.Trigger>
                      </Input>
                      <FileUpload.ClearTrigger asChild>
                        <PlusSvgIcon
                          position={'absolute'}
                          w='13px'
                          h='13px'
                          top='50%'
                          transform='translateY(-50%) rotate(45deg)'
                          transformOrigin={'top'}
                          right="0"
                          onClick={() => field.onChange(null)}
                        />
                      </FileUpload.ClearTrigger>
                      {!field.value && (
                        <PlusSvgIcon
                          position={'absolute'}
                          w='13px'
                          h='13px'
                          top='50%'
                          transform='translateY(-50%) rotate(0deg)'
                          transformOrigin={'center'}
                          right="0"
                        />
                      )}
                      <Field.ErrorText>{errors.resume?.message?.toString()}</Field.ErrorText>
                    </FileUpload.Root>
                  </Field.Root>
                )}
              />

              {/* MESSAGE */}
              <Field.Root invalid={!!errors.comment}>
                <Textarea
                  rows={0}
                  minH="40px"
                  maxH="120px"
                  resize="vertical"
                  maxLength={500}
                  {...register("comment")}
                  variant="flushed"
                  placeholder={t("career_comment")}
                  _placeholder={{
                    textStyle: 'medium16',
                    color: '#9E9F9D',
                    textTransform: 'uppercase',
                  }}
                />
                <Field.ErrorText>{errors.comment?.message}</Field.ErrorText>
              </Field.Root>

              <Button
                width={{ base: "100%" }}
                variant={"qr_primary"}
                autoFocus={false}
                textStyle="bold20"
                onClick={handleSubmit(onSubmit)}
              >
                {t("send")}
              </Button>
            </VStack>
          </VStack>
        </VStack>

        <Box
          w={"full"}
          aspectRatio={{ base: "412/234", md: "680/430" }}
          maxW={{ base: "100%", md: "50%" }}
          overflow={"hidden"}
          position={"relative"}
        >
          <picture>
            <source
              media="(max-width: 768px)"
              src={"/career/3.jpg"}
            />
        
            <Image
              src={"/career/3.jpg"}
              alt="Career"
              style={{ objectFit: "cover", objectPosition: "top" }}
              sizes="100vw"
              quality={70}
              priority
              fill
            />
          </picture>
        </Box>
      </HStack>
      <BaseDialog
        open={isSended}
        onOpenChange={() => setIsSended(false)}
        icon={<CheckSvgIcon />}
        footer={
          <Button
            width={{ base: "100%" }}
            variant="qr_secondary"
            autoFocus={false}
            onClick={() => {
              setIsSended(false);
            }}
          >
            {t("continue")}
          </Button>
        }
      >
        <Text>{modalText}</Text>
      </BaseDialog>
    </VStack>
  );
};

export default CareerForm;